/* ==========================================================================
   DeLusso — "Termékek" master–detail panel behaviour (concept 01)
   --------------------------------------------------------------------------
   Self-contained, no dependencies, no globals beyond one custom element.
   It only ever touches nodes inside its own `.dlm-panel` / `.dlm-trigger`.

   Opens on CLICK (never hover-only, so it works on touch and keyboard).
   Hover only moves the selection between departments, and only for a real
   mouse. Closes on: the trigger again, outside pointerdown, Escape (focus
   returns to the trigger), tabbing out of the panel, or following a link.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE_POINTER = function () {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  };

  function init(root) {
    var trigger = root.querySelector('.dlm-trigger');
    var panel = root.querySelector('.dlm-panel');
    if (!trigger || !panel || trigger.dataset.dlmReady === '1') return;
    trigger.dataset.dlmReady = '1';

    var tabs = Array.prototype.slice.call(panel.querySelectorAll('.dlm-tab'));
    var panes = tabs.map(function (t) { return document.getElementById(t.getAttribute('aria-controls')); });

    /* The backdrop lives on <body> so it can dim the whole page, and it is
       created lazily so a header without this menu adds nothing. */
    var backdrop = document.createElement('div');
    backdrop.className = 'dlm-backdrop';
    backdrop.hidden = true;
    document.body.appendChild(backdrop);

    var isOpen = false;
    var hoverTimer = null;

    /* ---- department selection (ARIA tabs, automatic activation) ---------- */
    function select(index, focusTab) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        tab.classList.toggle('is-active', on);
        if (panes[i]) panes[i].hidden = !on;
      });
      if (focusTab && tabs[index]) tabs[index].focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, true); });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        select(next, true);
      });
      tab.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse' || !FINE_POINTER()) return;
        window.clearTimeout(hoverTimer);
        /* Small delay so a diagonal sweep toward the links does not flip the
           department out from under the cursor. */
        hoverTimer = window.setTimeout(function () { select(i, false); }, 90);
      });
      tab.addEventListener('pointerleave', function () { window.clearTimeout(hoverTimer); });
    });

    /* ---- open / close ---------------------------------------------------- */
    function onDocPointerDown(e) {
      if (panel.contains(e.target) || trigger.contains(e.target)) return;
      close();
    }
    function onKeydown(e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      e.stopPropagation();
      close(true);
    }
    /* Tabbing past the last link closes the panel instead of leaving an
       invisible open menu behind the page. */
    function onFocusOut(e) {
      var next = e.relatedTarget;
      if (!next) return;
      if (panel.contains(next) || trigger.contains(next)) return;
      close();
    }
    function onPanelClick(e) {
      if (e.target.closest && e.target.closest('a[href]')) close();
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      panel.hidden = false;
      backdrop.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      /* Cap the panel to the space actually left below the header so a large
         taxonomy scrolls inside the panel instead of running off-screen. */
      var top = panel.getBoundingClientRect().top;
      panel.style.maxHeight = Math.max(240, Math.floor(window.innerHeight - top - 8)) + 'px';
      select(0, false);   /* never show a stale department */
      requestAnimationFrame(function () {
        /* Guard: closing within the same frame (Escape straight after opening,
           or a scripted open/close) would otherwise let this callback put the
           open state back on an already-closed panel. */
        if (!isOpen) return;
        panel.classList.add('is-open');
        backdrop.classList.add('is-open');
      });
      document.addEventListener('pointerdown', onDocPointerDown, true);
      document.addEventListener('keydown', onKeydown, true);
      panel.addEventListener('focusout', onFocusOut);
      panel.addEventListener('click', onPanelClick);
    }

    function close(restoreFocus) {
      if (!isOpen) return;
      isOpen = false;
      panel.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeydown, true);
      panel.removeEventListener('focusout', onFocusOut);
      panel.removeEventListener('click', onPanelClick);
      var finish = function () { if (!isOpen) { panel.hidden = true; backdrop.hidden = true; } };
      if (REDUCED) finish(); else window.setTimeout(finish, 260);
      if (restoreFocus) trigger.focus();
    }

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      isOpen ? close(true) : open();
    });
    backdrop.addEventListener('click', function () { close(); });
    /* A resize while open would leave a stale max-height. */
    window.addEventListener('resize', function () { if (isOpen) close(); });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.shopify-section--header'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* Re-init after a theme-editor section reload. */
  document.addEventListener('shopify:section:load', boot);
})();
